import type { Client } from '@efdigital/ll-addon-data';
import { DataTypeSystem } from '@efdigital/ll-addon-data/data-types';
import { debounce } from '@efdigital/ll-addon-ui/modifiers/debounce';
import { selectValue } from '@efdigital/ll-addon-ui/modifiers/select-value';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';
import type { ReactiveDataDocument } from '@warp-drive/core/reactive';
import { withReactiveResponse } from '@warp-drive/core/request';
import type { RequestInfo } from '@warp-drive/core/types/request';
import { query } from '@warp-drive/utilities/json-api';

import { filterRegex, page, sort } from '../../data/json-api.ts';
import { jsonApiOptions } from '../../lib/json-api.ts';

export interface SearchClientsState {
  page: number;
  pageSize: number;
  title: string;
  subscription: string;
  view: string;
  domain: string;
  connection: string;
  username: string;
  expires: string;
  sortField: string;
}

interface SearchClientsSignature {
  Element: HTMLDivElement;
  Args: {
    state: Readonly<SearchClientsState>;
    setState: (state: Partial<SearchClientsState>) => void;
  };
  Blocks: {
    content: [RequestInfo<ReactiveDataDocument<Client[]>>];
  };
}

export class SearchClients extends Component<SearchClientsSignature> {
  @service declare store: Store;

  get query() {
    const filter: Record<string, unknown> = {};

    if (this.args.state.title) {
      Object.assign(
        filter,
        filterRegex('filter[title]', this.args.state.title)
      );
    }
    if (this.args.state.subscription) {
      filter['filter[subscription]'] = this.args.state.subscription;
    }
    if (this.args.state.view) {
      filter['filter[view]'] = this.args.state.view;
    }
    if (this.args.state.domain) {
      filter['filter[domains]'] = this.args.state.domain;
    }
    if (this.args.state.connection) {
      filter['filter[connections]'] = this.args.state.connection;
    }
    if (this.args.state.username) {
      Object.assign(
        filter,
        filterRegex('filter[corporateUsers.username]', this.args.state.username)
      );
    }
    if (this.args.state.expires) {
      filter['filter[expires][$lte]'] = this.args.state.expires;
    }

    return withReactiveResponse<Client[]>(
      query(
        DataTypeSystem.Client,
        {
          ...filter,
          ...page({
            limit: this.args.state.pageSize,
            offset: (this.args.state.page - 1) * this.args.state.pageSize,
          }),
          ...sort(this.args.state.sortField || 'title'),
        },
        jsonApiOptions
      )
    );
  }

  updateTextFilter =
    (field: 'title' | 'domain' | 'connection' | 'username') =>
    (event: Event): void => {
      const target = event.target as HTMLInputElement;
      this.args.setState({
        [field]: target.value,
        page: 1,
      });
    };

  updateSelectFilter =
    (field: 'subscription' | 'view' | 'expires' | 'sortField') =>
    (event: Event): void => {
      const target = event.target as HTMLSelectElement;
      this.args.setState({
        [field]: target.value,
        page: 1,
      });
    };

  <template>
    <div class="card mb-4">
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-6">
            <label for="filter-title" class="form-label">Title</label>
            <input
              type="text"
              id="filter-title"
              class="form-control"
              value={{@state.title}}
              {{debounce (this.updateTextFilter "title")}}
              placeholder="Search by client title..."
            />
          </div>
          <div class="col-md-6">
            <label
              for="filter-subscription"
              class="form-label"
            >Subscription</label>
            <select
              id="filter-subscription"
              class="form-select"
              {{selectValue @state.subscription ""}}
              {{on "change" (this.updateSelectFilter "subscription")}}
            >
              <option value="">All</option>
              <option value="trial">Trial</option>
              <option value="selected">Selected Content</option>
              <option value="full">Standard</option>
              <option value="premium">Premium</option>
            </select>
          </div>
          <div class="col-md-4">
            <label for="filter-view" class="form-label">View</label>
            <select
              id="filter-view"
              class="form-select"
              {{selectValue @state.view ""}}
              {{on "change" (this.updateSelectFilter "view")}}
            >
              <option value="">All</option>
              <option value="va">Virtual Ashridge (Profile)</option>
              <option value="va-home">Virtual Ashridge (Home)</option>
              <option value="micro">Micro site</option>
              <option value="program">Program</option>
            </select>
          </div>
          <div class="col-md-4">
            <label for="filter-expires" class="form-label">Expires</label>
            <select
              id="filter-expires"
              class="form-select"
              {{selectValue @state.expires ""}}
              {{on "change" (this.updateSelectFilter "expires")}}
            >
              <option value="">All</option>
              <option value="past">Expired</option>
              <option value="future">Current</option>
              <option value="days-7">Expires within 7 days</option>
            </select>
          </div>
          <div class="col-md-4">
            <label for="sort" class="form-label">Sort by</label>
            <select
              id="sort"
              class="form-select"
              {{selectValue @state.sortField ""}}
              {{on "change" (this.updateSelectFilter "sortField")}}
            >
              <option value="title">Title</option>
              <option value="-modified">Modified (newest)</option>
              <option value="-expires">Expiry (newest)</option>
              <option value="expires">Expiry (oldest)</option>
            </select>
          </div>
          <div class="col-md-4">
            <label for="filter-domain" class="form-label">Email Domain</label>
            <input
              type="text"
              id="filter-domain"
              class="form-control"
              value={{@state.domain}}
              {{debounce (this.updateTextFilter "domain")}}
              placeholder="Search by domain..."
            />
          </div>
          <div class="col-md-4">
            <label for="filter-connection" class="form-label">Enterprise
              Connection</label>
            <input
              type="text"
              id="filter-connection"
              class="form-control"
              value={{@state.connection}}
              {{debounce (this.updateTextFilter "connection")}}
              placeholder="Search by connection..."
            />
          </div>
          <div class="col-md-4">
            <label for="filter-username" class="form-label">Corporate Username</label>
            <input
              type="text"
              id="filter-username"
              class="form-control"
              value={{@state.username}}
              {{debounce (this.updateTextFilter "username")}}
              placeholder="Search by username..."
            />
          </div>
        </div>
      </div>
    </div>

    {{yield this.query to="content"}}
  </template>
}

export default SearchClients;
